import { useState } from "react";
import { useCompanies } from "@/hooks/use-companies";
import { ScoreBadge } from "@/components/shared/score-badge";
import { formatCnpj } from "@/lib/utils";
import { GitCompare } from "lucide-react";

export function ComparisonPage() {
  const { data } = useCompanies();
  const [selected, setSelected] = useState<number[]>([]);

  const companies = data?.companies || [];

  const toggle = (id: number) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : prev.length < 5 ? [...prev, id] : prev
    );
  };

  const selectedCompanies = companies.filter((c) => selected.includes(c.id));

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Compare</h1>
        <p className="text-sm text-slate-500 mt-1">Select 2-5 companies for side-by-side comparison</p>
      </div>

      {/* Company selector */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex flex-wrap gap-2">
          {companies.map((c) => (
            <button
              key={c.id}
              onClick={() => toggle(c.id)}
              className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                selected.includes(c.id)
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
              }`}
            >
              {c.razao_social || formatCnpj(c.cnpj)}
            </button>
          ))}
          {companies.length === 0 && <p className="text-sm text-slate-400">Add companies to your portfolio first</p>}
        </div>
      </div>

      {/* Comparison table */}
      {selectedCompanies.length >= 2 ? (
        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500">Metric</th>
                {selectedCompanies.map((c) => (
                  <th key={c.id} className="px-6 py-3 text-left text-xs font-medium text-slate-500">
                    {c.razao_social || formatCnpj(c.cnpj)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              <tr>
                <td className="px-6 py-3 text-sm text-slate-700">Score</td>
                {selectedCompanies.map((c) => (
                  <td key={c.id} className="px-6 py-3">
                    <ScoreBadge score={c.score_overall} riskLevel={c.risk_level} />
                  </td>
                ))}
              </tr>
              <tr>
                <td className="px-6 py-3 text-sm text-slate-700">Status</td>
                {selectedCompanies.map((c) => (
                  <td key={c.id} className="px-6 py-3 text-sm">{c.situacao_cadastral || "--"}</td>
                ))}
              </tr>
              <tr>
                <td className="px-6 py-3 text-sm text-slate-700">State</td>
                {selectedCompanies.map((c) => (
                  <td key={c.id} className="px-6 py-3 text-sm">{c.uf || "--"}</td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 px-6 py-16 text-center">
          <GitCompare className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500">Select at least 2 companies to compare</p>
        </div>
      )}
    </div>
  );
}
